/**
 * Fusion Canvas (real case) — /cases/:id/canvas. Three tabs over the case (Intake
 * Builder): BUILD (author — card board; runs persist to case_events), CANVAS (the
 * projected graph from GET /api/v1/canvas/{id}/graph), JOURNAL (recap — reads the
 * persisted case_events). "Add demo surfaces" overlays the synthetic vision on the
 * Canvas tab. Isolated from the crypto Workbench (own store + own window.__fusionCy).
 *
 * Canvas aside mirrors /fusion: Inspector · Court-safe · Bank import · Case files
 * so a live statement lands on the real case graph and Evidence Locker.
 *
 * Macro view (C4-04): the envelope's declared phases become lanes, declared
 * (phase, group) bundles become group boxes with per-token totals, and the
 * Actions layer shows seizure targets as notice nodes (fusionMacro.ts). The
 * header runs the deep case worker as a background `case_worker` job (W3-07):
 * the job id rides the URL (`?job=`), its status is polled every 5 s and shown
 * in the header, and the graph refetches when it succeeds. On load the page
 * resumes a run that is still going (from `?job=`, else from the runs list), so
 * a reload keeps watching. If the async start fails with no answer or a server
 * error, the older synchronous request runs instead (progress from case events).
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowLeft, AlertTriangle, FileText, Play, Camera } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchCaseGraph, fetchCaseEvents, fetchCaseLinks, retryTransient, CaseGraphError, caseGraphErrorMessage, envelopeToElements, mergeWithDemo, formatUtcTime, formatUtcDateTime, toEpochMs,
  type CaseEventRow, type CaseGraph, type CaseGraphLevel,
} from '../services/caseCanvas';
import { fitTo } from '../components/fusion/fusionLayout';
import type { CaseLinkRow } from '../case-canvas/relatedCases';
import {
  decorateCaseElements, resolveCarriedSelection, expandableMembers, type CarriedSelection,
} from '../components/fusion/fusionMacro';
import { caseService, caseWorkerService, caseRunsService } from '../services/api';
import { useCanvasJobWatch } from '../components/cases/useCanvasJobWatch';
import { CASE_WORKER_JOB_EVENT, type CaseWorkerJobEventDetail } from '../case-canvas/jobs';
import { attachCanvasSnapshotToCase } from '../case-canvas/snapshot';
import {
  describeJobProgress, findActiveRun, runErrorMessage, secondsSince, startAutoInvestigateRun,
} from '../components/cases/caseRuns';
import { PageBreadcrumb } from '../components/PageBreadcrumb';
import { useFusionStore } from '../stores/useFusionStore';
import { readViewState, writeViewState } from '../case-canvas/viewState';
import { bareWalletAddress } from '../case-canvas/entityJoin';
import { useIntakeStore } from '../stores/useIntakeStore';
import { FusionToolbar } from '../components/fusion/FusionToolbar';
import { FusionCanvas } from '../components/fusion/FusionCanvas';
import { requestReveal } from '../components/fusion/fusionReveal';
import { FusionInspector } from '../components/fusion/FusionInspector';
import { FusionEvidenceLedger } from '../components/fusion/FusionEvidenceLedger';
import { FusionBankImport } from '../components/fusion/FusionBankImport';
import { FusionCaseFiles } from '../components/fusion/FusionCaseFiles';
import { FusionCanvasStage } from '../components/fusion/FusionCanvasStage';
import { FusionGraphLegend } from '../components/fusion/FusionGraphLegend';
import { RadialMenu } from '../components/fusion/RadialMenu';
import { RunRail } from '../components/fusion/RunRail';
import { IntakeBoard } from '../components/fusion/intake/IntakeBoard';
import { CaseFactsCard } from '../components/fusion/intake/CaseFactsCard';
import { Journal } from '../components/fusion/intake/Journal';
import { CaseReplay } from '../components/fusion/intake/CaseReplay';
import { ConsolidatedView } from '../case-canvas/consolidated/ConsolidatedView';
import {
  defaultCanvasView, dismissSummaryHint, initialCanvasView, rememberView, summaryHintDismissed, VIEW_LABEL,
  type CanvasViewChoice,
} from '../case-canvas/consolidated/defaultView';
import { clearCanvasFocus, focusMembersOnCanvas } from '../case-canvas/consolidated/focusEvidence';

import { isMainTab, resolveMainTab, type MainTab } from '../case-canvas/landingTab';
import { useCaseWriteBlock, useLoadCaseAccess } from '../case-canvas/caseAccess';
type AsideTab = 'inspector' | 'ledger' | 'import' | 'files';
type CanvasView = CanvasViewChoice;

// Synchronous fallback of the deep case-worker run: the same request, timeout
// and running flag as the case page (CaseDetail), one long POST with a 600 s
// server budget. The start time is kept in sessionStorage under the same key,
// so a reload (or the other page) neither double-runs nor loses the running
// state; it expires after the budget plus slack. The default path is the
// background job (`?job=`), which needs none of this.
const RUN_STALE_MS = 12 * 60 * 1000;
const RUN_POLL_MS = 5000;
const runFlagKey = (caseId: string) => `caseWorker.autoInvestigate.running.${caseId}`;

/** One line of run progress from the newest case event. */
function describeRunEvent(e: CaseEventRow): string {
  const p = (e.payload || {}) as Record<string, unknown>;
  const msg = [p.message, p.step, p.stage, p.status].find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  const when = formatUtcTime(e.ts);
  return `${e.event_type.replace(/_/g, ' ')}${msg ? `: ${msg}` : ''}${when ? ` · ${when}` : ''}`;
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

export default function CaseCanvasPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Opening a case lands on Canvas; only an explicit ?tab= names another tab.
  const [main, setMain] = useState<MainTab>(() => resolveMainTab(tabParam));
  // Aside tab and graph level are remembered per case for this browser tab (sessionStorage).
  const [tab, setTab] = useState<AsideTab>(() => readViewState(id)?.aside ?? 'inspector');
  const [graph, setGraph] = useState<CaseGraph | null>(null);
  const [loading, setLoading] = useState(true);
  // `err` is the FIRST-load failure (no graph to show). A failed refetch over a good
  // graph is `refreshErr`: the last graph stays mounted and a banner offers Retry.
  const [err, setErr] = useState<{ message: string; kind: string } | null>(null);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);
  const [retryNote, setRetryNote] = useState<string | null>(null);
  // Bumped on every successful load so the canvas remounts on new data, never on a failure.
  const [graphVersion, setGraphVersion] = useState(0);
  const graphRef = useRef<CaseGraph | null>(null);
  // Cases linked to this one (NS-58), for the related-case markers. Secondary: [] on any failure.
  const [caseLinks, setCaseLinks] = useState<CaseLinkRow[]>([]);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const ctl = new AbortController();
    setCaseLinks([]);
    void fetchCaseLinks(id, ctl.signal).then((rows) => { if (!cancelled) setCaseLinks(rows); });
    return () => { cancelled = true; ctl.abort(); };
  }, [id]);
  const [withDemo, setWithDemo] = useState(false);
  const [caseTitle, setCaseTitle] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // Graph altitude: 'low' (detail, default — identical to the Workbench
  // projection) or 'high' (macro: relay wallets folded into clusters).
  const [level, setLevel] = useState<CaseGraphLevel>(() => readViewState(id)?.level ?? 'low');
  // Members of a cluster the officer asked to expand; focused once the
  // detail-level graph has loaded.
  const [pendingFocusIds, setPendingFocusIds] = useState<string[] | null>(null);
  const focusParam = searchParams.get('focus');
  const appliedFocusRef = useRef<string | null>(null);
  // Actions layer (seizure targets → notice nodes); off by default.
  const [showActions, setShowActions] = useState(false);
  // Bumped to refetch the graph (e.g. after a case-worker run lands).
  const [graphNonce, setGraphNonce] = useState(0);
  // OPEN01: the detail level opens on the spine (role-bearing nodes). The full projection is fetched when something
  // needs a node the spine left out (a focus, a cluster expand, a Summary jump) or the officer opens the context count.
  const [fullRequested, setFullRequested] = useState(false);
  const [contextRequested, setContextRequested] = useState(false);
  // Evidence (today's canvas) or the consolidated bucket view of one run (?run=, default latest).
  // Decided before any canvas mounts: ?view=, else the last choice (this case, then this
  // officer), else the default (Full evidence). The graph never changes it on its own.
  const viewParam = searchParams.get('view');
  const [view, setView] = useState<CanvasView>(() => initialCanvasView({ viewParam, caseId: id }));
  const [hintDismissed, setHintDismissed] = useState(() => summaryHintDismissed(id));
  const chooseView = useCallback((next: CanvasView) => {
    rememberView(id, next);
    setView(next);
  }, [id]);
  const onClearCanvasFocus = useCallback(() => {
    const cy = (window as any).__fusionCy;
    if (cy && !cy.destroyed?.()) clearCanvasFocus(cy);
  }, []);
  const consolRunId = searchParams.get('run') || 'latest';
  // Member identifiers to focus on the evidence canvas after a switch from the consolidated view.
  const [pendingConsolFocus, setPendingConsolFocus] = useState<Set<string> | null>(null);
  // Selection carried across a level switch / layer toggle (both remount the canvas).
  const carryRef = useRef<CarriedSelection | null>(null);

  // ── Deep case-worker run (shared running flag with CaseDetail) ──
  const runKey = id ? runFlagKey(id) : null;
  const readRunStart = useCallback((): number | null => {
    if (!runKey) return null;
    try {
      const raw = sessionStorage.getItem(runKey);
      const started = raw ? Number(raw) : NaN;
      if (!Number.isFinite(started)) return null;
      if (Date.now() - started > RUN_STALE_MS) {
        sessionStorage.removeItem(runKey);
        return null;
      }
      return started;
    } catch {
      return null;
    }
  }, [runKey]);
  const [runStartedAt, setRunStartedAt] = useState<number | null>(() => readRunStart());
  const [runElapsed, setRunElapsed] = useState(0);
  const [runProgress, setRunProgress] = useState<string | null>(null);
  const runInFlightRef = useRef(false);   // this tab holds the POST promise

  // ── Background job (default path): the job id lives in the URL (?job=) ──
  const jobParam = searchParams.get('job');
  const [startingRun, setStartingRun] = useState(false);
  const [jobClock, setJobClock] = useState(() => Date.now());
  const jobWatchStartRef = useRef(Date.now());
  const startedJobsRef = useRef<Set<string>>(new Set());   // jobs started from this page or its rail
  const running = runStartedAt !== null || !!jobParam || startingRun;
  // A viewer collaborator reads the case and never acts on it (ACC03): Run, Attach and the Build / palette controls are disabled with the reason.
  useLoadCaseAccess(id);
  const { blocked: writeBlocked, reason: writeReason } = useCaseWriteBlock();

  const setJobParam = useCallback((jobId: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (jobId) p.set('job', jobId);
      else p.delete('job');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => { jobWatchStartRef.current = Date.now(); }, [jobParam]);

  // On load (no ?job=): resume a run that is still going for this case.
  useEffect(() => {
    if (!id || searchParams.get('job')) return;
    let cancelled = false;   // R8: an answer for another case never lands
    caseRunsService.list(id, 10)
      .then((res) => {
        if (cancelled) return;
        const active = findActiveRun(res?.runs);
        if (active?.job_id) setJobParam(active.job_id);
      })
      .catch(() => { /* the runs list is optional here */ });
    return () => { cancelled = true; };
    // Only on case change: a run started from this page sets ?job= itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const jobWatch = useCanvasJobWatch(id, jobParam, {
    onDone: (job) => {
      setJobParam(null);
      // A job that had already ended before this page watched it (a stale
      // ?job= link) only refreshes the graph; it does not announce itself.
      const endedMs = job.ended_at ? Date.parse(job.ended_at) : NaN;
      const stale = Number.isFinite(endedMs) && endedMs < jobWatchStartRef.current - 5000;
      if (job.status === 'succeeded') {
        setGraphNonce((n) => n + 1);
        if (!stale) toast.success('Case worker finished. Graph refreshed.');
      } else if (!stale) {
        if (job.status === 'cancelled') toast.warning('The case-worker run was cancelled before it finished.');
        else toast.error(`Case worker failed: ${job.error || 'no error text was recorded'}`);
      }
    },
    onLost: (message) => {
      // Only a run started here is announced; a resumed one drops quietly.
      if (jobParam && startedJobsRef.current.has(jobParam)) {
        toast.error(`Stopped following the case-worker run (${message}).`);
      }
      setJobParam(null);
    },
  });

  // A run started from the operator rail (O12): follow it in the header too.
  useEffect(() => {
    const onRailJob = (e: Event) => {
      const detail = (e as CustomEvent<CaseWorkerJobEventDetail>).detail;
      if (!detail?.jobId || detail.caseId !== id) return;
      e.preventDefault();   // this page refetches the graph when the job succeeds
      startedJobsRef.current.add(detail.jobId);
      setJobParam(detail.jobId);
    };
    window.addEventListener(CASE_WORKER_JOB_EVENT, onRailJob);
    return () => window.removeEventListener(CASE_WORKER_JOB_EVENT, onRailJob);
  }, [id, setJobParam]);

  useEffect(() => {
    if (!jobParam) return;
    setJobClock(Date.now());
    const timer = window.setInterval(() => setJobClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [jobParam]);

  const finishRun = useCallback((refresh: boolean) => {
    try { if (runKey) sessionStorage.removeItem(runKey); } catch { /* ignore */ }
    setRunStartedAt(null);
    setRunProgress(null);
    if (refresh) setGraphNonce((n) => n + 1);
  }, [runKey]);

  useEffect(() => { setRunStartedAt(readRunStart()); }, [readRunStart]);

  useEffect(() => {
    if (runStartedAt === null) { setRunElapsed(0); return; }
    const tick = () => {
      const elapsed = Date.now() - runStartedAt;
      setRunElapsed(Math.floor(elapsed / 1000));
      // A run restored after a reload has no promise to settle it; release the
      // button once the server budget has certainly passed.
      if (elapsed > RUN_STALE_MS) finishRun(false);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [runStartedAt, finishRun]);

  // Progress: poll the case events every 5 s while a run is going. A
  // `case_worker_run` event newer than the start settles a run this tab does
  // not hold the promise for (restored after a reload, or a browser timeout).
  useEffect(() => {
    if (!id || runStartedAt === null) return;
    let cancelled = false;
    const since = runStartedAt - 5000;
    const poll = async () => {
      try {
        const events = await fetchCaseEvents(id, 20);
        if (cancelled) return;
        const fresh = events.filter((e) => {
          const ms = toEpochMs(e.ts);
          return ms !== null && ms >= since;
        });
        if (fresh.length) setRunProgress(describeRunEvent(fresh[0]));
        const done = fresh.find((e) => e.event_type === 'case_worker_run');
        if (done && !runInFlightRef.current) {
          finishRun(true);
          toast.success('Case-worker run recorded. Graph refreshed.');
        }
      } catch { /* progress is best-effort */ }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, RUN_POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [id, runStartedAt, finishRun]);

  const handleRunDeep = async () => {
    if (!id || !runKey || writeBlocked) return;
    if (jobParam || startingRun || readRunStart() !== null) {
      toast.info('A case-worker run for this case is already running.');
      return;
    }
    const caseId = id;
    setStartingRun(true);
    try {
      const outcome = await startAutoInvestigateRun(
        () => caseWorkerService.startAutoInvestigateJob(caseId, caseTitle ? { case_title: caseTitle } : {}),
        () => { setStartingRun(false); return runDeepSync(); },
      );
      if (outcome.mode === 'job') {
        startedJobsRef.current.add(outcome.job.job_id);
        setJobParam(outcome.job.job_id);
        toast.info(outcome.job.existing
          ? 'A case-worker run is already going for this case; showing its progress.'
          : 'Case worker started (deep). This can take several minutes; progress shows in the header.');
      } else if (outcome.via === 'inline') {
        toast.success('Case worker finished. Graph refreshed.');
        setGraphNonce((n) => n + 1);
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const msg = runErrorMessage(e);
      if (status === 409) toast.info(`A case-worker run is already in progress for this case: ${msg}`);
      else toast.error(`Case worker could not start: ${msg}`);
    } finally {
      setStartingRun(false);
    }
  };

  /** Synchronous fallback: one long POST; progress from the case events. */
  const runDeepSync = async (): Promise<unknown> => {
    if (!id || !runKey) return null;
    const startedAt = Date.now();
    try { sessionStorage.setItem(runKey, String(startedAt)); } catch { /* private mode */ }
    setRunStartedAt(startedAt);
    runInFlightRef.current = true;
    toast.info('Case worker started (deep, direct request). This can take several minutes; progress shows in the header.');
    try {
      // The server hydrates seeds and notes from the case and its watchlist.
      const result = await caseWorkerService.autoInvestigateCase(id, caseTitle ? { case_title: caseTitle } : {});
      const sum = (result && typeof result === 'object' ? (result as Record<string, unknown>).summary : null) as Record<string, unknown> | null;
      const parts: string[] = [];
      if (sum && typeof sum.crypto_targets_count === 'number') parts.push(`${sum.crypto_targets_count} crypto targets`);
      if (sum && typeof sum.flow_edges === 'number') parts.push(`${sum.flow_edges} flow edges`);
      if (sum && typeof sum.seizure_targets_count === 'number') parts.push(`${sum.seizure_targets_count} seizure targets`);
      toast.success(`Case worker finished${parts.length ? `: ${parts.join(', ')}` : ''}. Graph refreshed.`);
      finishRun(true);
      return result;
    } catch (e: unknown) {
      const err = e as { code?: string; message?: string; response?: { data?: { detail?: string } } };
      const timedOut = err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
      if (timedOut) {
        // The server may still be writing results: keep the running flag and
        // keep polling; the run settles when its case event lands.
        toast.info('The request timed out in the browser; the server may still be writing results. Watching the case events.');
      } else {
        toast.error(`Case worker error: ${runErrorMessage(e)}`);
        finishRun(false);
      }
      return null;
    } finally {
      runInFlightRef.current = false;
    }
  };

  const openReport = useCallback(() => {
    if (!id) return;
    window.open(`/cases/${encodeURIComponent(id)}/report`, '_blank', 'noopener,noreferrer');
  }, [id]);

  // Store a picture of the current board as case evidence; the case report shows the latest one.
  const [attachingSnapshot, setAttachingSnapshot] = useState(false);
  const attachSnapshot = useCallback(async () => {
    const cy = (window as unknown as { __fusionCy?: import('cytoscape').Core }).__fusionCy;
    if (!id || !cy || withDemo || writeBlocked) return;
    setAttachingSnapshot(true);
    try {
      await attachCanvasSnapshotToCase(cy, {
        caseId: id, courtSafe: useFusionStore.getState().courtSafe, level, addEvidence: caseService.addEvidence,
      });
      toast.success('Snapshot attached. The case report shows it under "Case canvas".');
    } catch (e) {
      toast.error(`The snapshot was not attached: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setAttachingSnapshot(false);
    }
  }, [id, withDemo, level, writeBlocked]);

  /** Remember the selected node so it can be re-selected after a remount. */
  const captureSelection = useCallback(() => {
    const sel = useFusionStore.getState().selected;
    if (!sel || sel.kind !== 'node') { carryRef.current = null; return; }
    const cy = (window as any).__fusionCy;
    const ele = cy && !cy.destroyed?.() ? cy.getElementById(sel.id) : null;
    const data = ele && ele.length ? (ele.data() as Record<string, unknown>) : null;
    carryRef.current = { id: sel.id, members: expandableMembers(data) };
  }, []);

  // Deep-link ?tab=build|canvas|journal (e.g. after New Fusion case).
  useEffect(() => {
    const t = searchParams.get('tab');
    if (isMainTab(t)) setMain(t);
  }, [searchParams]);

  // Switching to another case inside the same page instance opens that case on Canvas too.
  const lastCaseRef = useRef(id);
  useEffect(() => {
    if (lastCaseRef.current === id) return;
    lastCaseRef.current = id;
    setMain(resolveMainTab(searchParams.get('tab')));
  }, [id]);

  const goMain = useCallback((next: MainTab) => {
    setMain(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === 'canvas') p.delete('tab');
      else p.set('tab', next);
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // Normalise the shared store + bind this case for intake (runs persist to case_events).
  useEffect(() => {
    const s = useFusionStore.getState(); s.setStep(1); s.setPlaying(false); s.select(null);
    useIntakeStore.getState().setCaseId(id);
    useIntakeStore.getState().stopReplay();
    if (id) {
      void import('../case-canvas/intake/journalSpine').then(async (j) => {
        try {
          await j.flushOutbox(id);
          const entries = await j.fetchCaseEvents(id);
          j.writeEventsCache(id, entries);
        } catch { /* Journal loads on-demand */ }
      });
    }
    return () => { useIntakeStore.getState().setCaseId(undefined); useIntakeStore.getState().stopReplay(); };
  }, [id]);

  // Journal "Replay" switches here so the rebuild is visible on the canvas.
  useEffect(() => {
    const toCanvas = () => goMain('canvas');
    window.addEventListener('intake:goto-canvas', toCanvas);
    return () => window.removeEventListener('intake:goto-canvas', toCanvas);
  }, [goMain]);

  useEffect(() => {
    if (!id) return;
    caseService.get(id).then((kase) => {
      if (kase?.title) setCaseTitle(kase.title);
    }).catch(() => { /* non-fatal */ });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    // R8: a slow response for the other level / case never lands (abort + flag).
    const ctl = new AbortController();
    let cancelled = false;
    // A graph from another case is never shown under this one.
    const held = graphRef.current && graphRef.current.case_id === id ? graphRef.current : null;
    if (!held && graphRef.current) { graphRef.current = null; setGraph(null); }
    setLoading(true); setErr(null); setRefreshErr(null); setRetryNote(null);
    // First load: two retries (1 s, 3 s) for transient failures, 60 s per attempt.
    // Refetch over a good graph: one attempt; a failure keeps what is on screen.
    const attempt = () => fetchCaseGraph(id, { level, spine: level === 'low' && !fullRequested && !focusParam, signal: ctl.signal });
    (held ? attempt() : retryTransient(attempt, {
      signal: ctl.signal,
      onRetry: (n, of) => { if (!cancelled) setRetryNote(`The server did not answer, retrying (${n} of ${of})`); },
    }))
      .then((g) => {
        if (cancelled) return;
        graphRef.current = g;
        setGraph(g); setGraphVersion((v) => v + 1); setRetryNote(null); setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        const kind = e instanceof CaseGraphError ? e.kind : 'network';
        if (kind === 'aborted') return;
        const message = e instanceof CaseGraphError ? e.message : caseGraphErrorMessage('network');
        if (held) setRefreshErr(message); else setErr({ message, kind });
        setRetryNote(null);
        setLoading(false);
      });
    return () => { cancelled = true; ctl.abort(); };
  }, [id, level, graphNonce, fullRequested]);

  // Switching cases re-decides from what was remembered for the new case. An explicit
  // ?view= on the new case wins. Decided from storage only, never from the graph.
  const seenCaseRef = useRef(id);
  const skipViewSaveRef = useRef(false);
  useEffect(() => {
    if (seenCaseRef.current === id) return;
    seenCaseRef.current = id;
    setView(initialCanvasView({ viewParam: searchParams.get('view'), caseId: id }));
    setHintDismissed(summaryHintDismissed(id));
    skipViewSaveRef.current = true;
    setFullRequested(false); setContextRequested(false);
    const remembered = readViewState(id);
    setLevel(remembered?.level ?? 'low');
    setTab(remembered?.aside ?? 'inspector');
  }, [id, searchParams]);

  // Write level and aside tab as they change. The first pass after a case change is skipped: the
  // state still holds the previous case's values until the case-change effect above has run.
  useEffect(() => {
    if (!id) return;
    if (skipViewSaveRef.current) { skipViewSaveRef.current = false; return; }
    writeViewState(id, { level, aside: tab });
  }, [id, level, tab]);

  // Journal replay step, remembered per case (written only while a replay is running).
  useEffect(() => {
    if (!id) return undefined;
    return useIntakeStore.subscribe((s, prev) => {
      if (s.replay && s.replay.step !== prev.replay?.step) writeViewState(id, { replayStep: s.replay.step });
    });
  }, [id]);

  // A summary exists for a case-worker case above ~60 nodes: say so, never switch on its own.
  const summarySuggested = view === 'evidence' && !hintDismissed && !loading && !!graph
    && defaultCanvasView({
      viewParam: null,
      nodeCount: graph.spine?.total_nodes ?? graph.nodes?.length ?? 0,
      hasCaseWorkerRun: graph.latest_run != null,
    }) === 'consolidated';

  // The graph in state belongs to this altitude (guards the render between a
  // level switch and the new fetch starting).
  const graphLevel: CaseGraphLevel = graph?.level === 'high' ? 'high' : 'low';
  const graphMatchesLevel = !!graph && graphLevel === level;

  // Deep-link ?focus=<bare wallet address> — the Workbench → Fusion return path
  // (fusionLink.buildFusionCanvasUrl). Once the graph is loaded, select + center
  // the spine node whose address matches, so the officer lands on the same wallet
  // they were viewing on-chain. Matches on the BARE address (chain-prefix agnostic).
  // Runs once per focus value (ref guard) and removes `focus` from the URL when
  // done, so later tab changes never bounce back to the Canvas tab.
  useEffect(() => {
    if (!focusParam || loading || err || !graph) return;
    if (appliedFocusRef.current === focusParam) return;
    const target = bareWalletAddress(focusParam);
    const match = target ? (graph.nodes || []).find((n: any) =>
      [n.address, n.canonical_id, n.id]
        .filter(Boolean)
        .some((c: string) => bareWalletAddress(String(c)) === target),
    ) : undefined;
    appliedFocusRef.current = focusParam;
    const clearFocusParam = () => setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('focus');
      return p;
    }, { replace: true });
    if (!match) { clearFocusParam(); return; }
    const matchId = String((match as any).id);
    requestReveal([matchId]);   // the wallet may sit in a folded group box
    setMain('canvas');
    useFusionStore.getState().select({ id: matchId, kind: 'node' });
    // Best-effort center once the Fusion cytoscape instance has mounted.
    let tries = 0;
    let timer: number | undefined;
    const tick = () => {
      const cy = (window as any).__fusionCy;
      const ele = cy?.getElementById?.(matchId);
      if (cy && ele && ele.length) {
        try {
          cy.animate({ center: { eles: ele }, zoom: Math.max(cy.zoom?.() ?? 1, 1) }, { duration: 300 });
        } catch { /* centering is non-fatal */ }
        clearFocusParam();
        return;
      }
      if (tries++ < 20) timer = window.setTimeout(tick, 150);
      else clearFocusParam();
    };
    tick();
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, [focusParam, loading, err, graph, setSearchParams]);

  // Macro view → expand a cluster: switch to the detail level, then focus its members.
  const expandCluster = useCallback((memberIds: string[]) => {
    if (!memberIds.length) return;
    requestReveal(memberIds);   // the members may sit in folded group boxes
    setPendingFocusIds(memberIds);
    setFullRequested(true);
    useFusionStore.getState().select(null);
    setLevel('low');
  }, []);

  useEffect(() => {
    if (!pendingFocusIds || level !== 'low' || loading || err || !graph || !graphMatchesLevel) return;
    const wanted = new Set(pendingFocusIds);
    let tries = 0;
    let timer: number | undefined;
    const tick = () => {
      const cy = (window as any).__fusionCy;
      if (cy && !cy.destroyed?.()) {
        const members = cy.nodes().filter((n: any) => wanted.has(String(n.id())));
        if (members.length) {
          cy.elements().unselect();
          members.select();
          useFusionStore.getState().select({ id: String(members[0].id()), kind: 'node' });
          fitTo(cy, members, 80);
          setPendingFocusIds(null);
          return;
        }
      }
      if (tries++ < 20) timer = window.setTimeout(tick, 150);
      else setPendingFocusIds(null);
    };
    tick();
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, [pendingFocusIds, level, loading, err, graph, graphMatchesLevel]);

  // Re-select the carried node after a level switch or layer toggle: the same
  // id, else the cluster / group box now holding it, else the first member of
  // the old cluster (R8: the cleanup cancels a stale attempt).
  useEffect(() => {
    if (!carryRef.current || loading || err || !graph || !graphMatchesLevel || pendingFocusIds) return;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    const tick = () => {
      if (cancelled) return;
      const cy = (window as any).__fusionCy;
      if (cy && !cy.destroyed?.()) {
        const nodes = cy.nodes().map((n: any) => n.data() as Record<string, unknown>);
        const target = resolveCarriedSelection(nodes, carryRef.current);
        carryRef.current = null;
        if (target) {
          cy.elements().unselect();
          cy.getElementById(target).select();
          useFusionStore.getState().select({ id: target, kind: 'node' });
        }
        return;
      }
      if (tries++ < 20) timer = window.setTimeout(tick, 150);
      else carryRef.current = null;
    };
    tick();
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, [graph, graphMatchesLevel, showActions, loading, err, pendingFocusIds]);

  // Consolidated -> Evidence focus: members VISIBLE, the rest DIMMED via _visibility (R11).
  // Waits for the remounted Fusion canvas to hold the members (R8: cleanup cancels).
  useEffect(() => {
    if (!pendingConsolFocus || view !== 'evidence' || main !== 'canvas' || loading || err || !graph) return;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    const tick = () => {
      if (cancelled) return;
      const cy = (window as any).__fusionCy;
      if (cy && !cy.destroyed?.() && focusMembersOnCanvas(cy, pendingConsolFocus)) {
        setPendingConsolFocus(null);
        return;
      }
      if (tries++ < 20) timer = window.setTimeout(tick, 150);
      else {
        setPendingConsolFocus(null);
        toast.info('None of those wallets are on the evidence canvas at this level.');
      }
    };
    tick();
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, [pendingConsolFocus, view, main, loading, err, graph]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    goMain('canvas');
    setTab('import');
    window.dispatchEvent(new CustomEvent('fusion:drop-file', { detail: f }));
  }, [goMain]);

  const realCount = graph?.spine?.total_nodes ?? graph?.nodes?.length ?? 0;
  const realEdgeCount = graph?.spine?.total_edges ?? graph?.edges?.length ?? 0;
  const seizureCount = graph?.seizure_targets?.length ?? 0;
  const elements = useMemo(() => {
    if (!graph) return [];
    const real = decorateCaseElements(
      envelopeToElements(graph.nodes || [], graph.edges || []),
      graph,
      { showActions },
    );
    if (!withDemo) return real;
    return mergeWithDemo(real, graph.nodes?.[0]?.id);
  }, [graph, withDemo, showActions]);
  const lastRunAt = graph?.latest_run?.ts ? formatUtcDateTime(graph.latest_run.ts) : null;
  const jobStartIso = jobWatch.job?.started_at || jobWatch.job?.created_at || null;
  const jobElapsed = jobStartIso ? secondsSince(jobStartIso, jobClock) : null;
  const jobProgressText = describeJobProgress(jobWatch.job?.progress);

  const emptyReal = !loading && !err && realCount === 0;
  const showLegend = useFusionStore((s) => s.showLegend);
  const MAIN_TABS: { id: MainTab; label: string }[] = [
    { id: 'build', label: 'Build' }, { id: 'canvas', label: 'Canvas' }, { id: 'journal', label: 'Journal' },
  ];

  return (
    <div className="absolute inset-0 flex flex-col min-h-0 overflow-hidden bg-background">
      <div className="px-4 py-2 border-b border-border bg-card/50 shrink-0">
        <PageBreadcrumb
          className="mb-1"
          items={[
            { label: 'Dashboard', href: '/' },
            { label: 'Cases', href: '/cases' },
            { label: caseTitle || 'Case', href: id ? `/cases/${id}` : undefined },
            { label: 'Fusion Canvas' },
          ]}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={() => nav(`/cases/${id}`)}
            className="flex items-center gap-1.5 p-1.5 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
            title="Back to case"
            aria-label="Back to case file"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium pr-1">Case file</span>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold truncate leading-tight">Fusion Canvas</h1>
            <p className="text-xs text-muted-foreground font-mono truncate">
              case {id}{graph ? ` · ${realCount} nodes · ${realEdgeCount} edges · ${graph.counts?.investigations ?? 0} investigations` : ''}
              {graph && level === 'high' && graph.counts?.collapsed_members
                ? ` · ${graph.counts.collapsed_members} relay wallets in ${graph.counts.collapsed_clusters ?? 0} cluster(s)`
                : ''}
              {graph?.phases?.length ? ` · ${graph.phases.length} phase lane(s)` : ''}
              {graph ? (lastRunAt ? ` · last case-worker run ${lastRunAt}` : ' · no case-worker run recorded') : ''}
            </p>
            {jobParam ? (
              <p className="text-[11px] text-primary truncate" data-testid="case-worker-progress" aria-live="polite" data-job-id={jobParam}>
                Case worker running{jobElapsed !== null ? ` · ${formatElapsed(jobElapsed)}` : ''}
                {' · '}{jobProgressText || 'waiting for the first step'}
                {jobWatch.error ? ' · status check failed, retrying' : ''}
              </p>
            ) : runStartedAt !== null ? (
              <p className="text-[11px] text-primary truncate" data-testid="case-worker-progress" aria-live="polite">
                Case worker running · {formatElapsed(runElapsed)}{runProgress ? ` · ${runProgress}` : ' · waiting for the first case event'}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleRunDeep}
            disabled={running || !id || writeBlocked}
            data-testid="case-worker-deep-run"
            data-tour="case-worker-deep-run"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap bg-secondary hover:bg-secondary/80 text-foreground border-border disabled:opacity-60 disabled:cursor-not-allowed"
            title={writeBlocked ? writeReason : running ? 'A case-worker run is in progress for this case' : 'Run the case worker in deep mode on this case (several minutes)'}
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> : <Play className="w-3.5 h-3.5" aria-hidden />}
            {startingRun ? 'Starting…' : running ? 'Case worker running' : 'Run case worker (deep)'}
          </button>
          <button
            type="button"
            onClick={openReport}
            disabled={!id}
            data-testid="case-report-open"
            data-tour="case-report-open"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap bg-secondary hover:bg-secondary/80 text-foreground border-border disabled:opacity-60"
            title="Open the case report in a new tab (printable)"
          >
            <FileText className="w-3.5 h-3.5" aria-hidden /> Report
          </button>
          <button
            type="button"
            onClick={attachSnapshot}
            disabled={!id || main !== 'canvas' || withDemo || attachingSnapshot || writeBlocked}
            data-testid="case-canvas-attach-snapshot"
            data-tour="case-canvas-attach-snapshot"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap bg-secondary hover:bg-secondary/80 text-foreground border-border disabled:opacity-60 disabled:cursor-not-allowed"
            title={writeBlocked ? writeReason : withDemo ? 'Turn off demo surfaces to attach the board to the case report'
              : main !== 'canvas' ? 'Open the Canvas tab to attach the board to the case report'
                : 'Store a picture of the current board as case evidence; the case report shows the latest one'}
          >
            {attachingSnapshot ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> : <Camera className="w-3.5 h-3.5" aria-hidden />}
            {attachingSnapshot ? 'Attaching…' : 'Attach to report'}
          </button>
          {main === 'canvas' && (
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-2 py-1 rounded-md border border-border bg-secondary/30">
              <input type="checkbox" checked={withDemo} onChange={(e) => setWithDemo(e.target.checked)} />
              Add demo surfaces
            </label>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-border bg-card/50 text-sm shrink-0 px-2 min-h-[2.75rem]">
        <div className="flex items-stretch shrink-0">
          {MAIN_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => goMain(t.id)}
              className={`px-4 py-2 font-medium transition-colors ${main === t.id ? 'border-b-2 border-primary text-foreground bg-secondary/30' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {main === 'canvas' && (
          <div className="ml-auto flex items-center gap-2">
          {view === 'evidence' && (
            <button
              type="button"
              data-tour="case-clear-focus"
              data-testid="case-clear-focus"
              onClick={onClearCanvasFocus}
              className="rounded-md border border-border bg-secondary/30 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              Clear focus
            </button>
          )}
          <div
            role="radiogroup"
            aria-label="Canvas view"
            data-tour="case-consolidated-toggle"
            data-testid="case-consolidated-toggle"
            className="flex items-center rounded-md border border-border bg-secondary/30 p-0.5 text-xs"
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                chooseView(view === 'evidence' ? 'consolidated' : 'evidence');
              }
            }}
          >
            {(['evidence', 'consolidated'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={view === v}
                tabIndex={view === v ? 0 : -1}
                data-testid={`case-view-${v}`}
                onClick={() => chooseView(v)}
                className={`rounded px-3 py-1 font-medium transition-colors ${view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {VIEW_LABEL[v]}
              </button>
            ))}
          </div>
          </div>
        )}
      </div>
      {main === 'canvas' && summarySuggested && (
        <div
          data-testid="case-summary-hint"
          className="shrink-0 flex items-center gap-3 px-4 py-1.5 text-xs border-b border-border bg-primary/5"
        >
          <span className="text-foreground">
            This case has a run with {graph?.nodes?.length ?? 0} nodes. The Summary view groups it into a few buckets; Full evidence shows every node.
          </span>
          <button
            type="button"
            data-testid="case-summary-hint-open"
            onClick={() => chooseView('consolidated')}
            className="rounded border border-border bg-secondary px-2 py-1 font-medium hover:bg-secondary/80"
          >
            Open Summary view
          </button>
          <button
            type="button"
            data-testid="case-summary-hint-dismiss"
            onClick={() => { dismissSummaryHint(id); setHintDismissed(true); }}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      )}
      {main === 'canvas' && view === 'consolidated' && id && (
        <div className="relative flex-1 min-h-0" data-testid="case-consolidated-pane">
          <ConsolidatedView
            caseId={id}
            runId={consolRunId}
            noRunRecorded={!!graph && !graph.latest_run}
            onBackToEvidence={() => chooseView('evidence')}
            onShowOnEvidence={(keys) => { setPendingConsolFocus(keys); setFullRequested(true); chooseView('evidence'); }}
          />
        </div>
      )}
      {main === 'canvas' && view === 'evidence' && (
        <FusionToolbar
          exportMode="real"
          exportCaseId={id}
          exportDisabled={withDemo}
          level={level}
          onLevelChange={(next) => {
            captureSelection();
            setPendingFocusIds(null);
            useFusionStore.getState().select(null);
            setLevel(next);
          }}
          levelBusy={loading}
          actionsOn={showActions}
          actionsCount={seizureCount}
          onActionsChange={(on) => {
            captureSelection();
            useFusionStore.getState().select(null);
            setShowActions(on);
          }}
        />
      )}

      {main === 'build' && (
        <div className="flex-1 min-h-0 flex flex-col">
          {id && <CaseFactsCard caseId={id} />}
          <div className="flex-1 min-h-0"><IntakeBoard /></div>
        </div>
      )}
      {main === 'journal' && <div className="flex-1 min-h-0"><Journal /></div>}

      {main === 'canvas' && view === 'evidence' && (
        <>
          <FusionCanvasStage
            forceAsideExpanded={tab === 'ledger' || tab === 'import' || tab === 'files'}
            aside={(
              <>
                <div className="flex border-b border-border text-xs shrink-0 bg-card/50 overflow-x-auto">
                  <button onClick={() => setTab('inspector')} data-tour="fusion-aside-inspector"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'inspector' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Inspector
                  </button>
                  <button onClick={() => setTab('ledger')} data-tour="fusion-aside-ledger"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'ledger' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Court-safe
                  </button>
                  <button onClick={() => setTab('import')} data-tour="fusion-aside-import"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'import' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Bank import
                  </button>
                  <button onClick={() => setTab('files')} data-tour="fusion-aside-files"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'files' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Case files
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {tab === 'inspector' && <FusionInspector caseId={id} onExpandCluster={expandCluster} />}
                  {tab === 'ledger' && <FusionEvidenceLedger source="canvas" onClose={() => setTab('inspector')} />}
                  {tab === 'import' && <FusionBankImport onOpenCaseFiles={() => setTab('files')} />}
                  {tab === 'files' && <FusionCaseFiles />}
                </div>
              </>
            )}
          >
            <div
              className="absolute inset-0"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              {loading && !graph && (
                <div className="absolute inset-0 grid place-items-center text-muted-foreground text-sm bg-canvas/90" data-testid="case-graph-loading">
                  <span className="flex flex-col items-center gap-1">
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading case graph…</span>
                    {retryNote && <span className="text-xs text-amber-500" role="status" data-testid="case-graph-retry-note">{retryNote}</span>}
                  </span>
                </div>
              )}
              {loading && graph && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 text-xs text-muted-foreground bg-card border border-border rounded-md px-3 py-1.5 shadow-overlay" data-testid="case-graph-refreshing">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Refreshing case graph…
                </div>
              )}
              {err && (
                <div className="absolute inset-0 grid place-items-center bg-canvas/90 p-6" data-testid="case-graph-error">
                  <div role="alert" className="max-w-md w-full text-center bg-card border border-border rounded-lg px-6 py-6 shadow-overlay">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-500" />
                    <p className="text-sm font-medium text-foreground">Could not load this case</p>
                    <p className="text-sm text-muted-foreground mt-1" data-testid="case-graph-error-message">{err.message}</p>
                    <div className="mt-4 flex justify-center gap-2">
                      {err.kind !== 'forbidden' && err.kind !== 'not_found' && err.kind !== 'unauthorized' && (
                        <button
                          type="button"
                          data-testid="case-graph-retry"
                          onClick={() => setGraphNonce((n) => n + 1)}
                          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-brand-gold-hover transition-colors"
                        >
                          Retry
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => nav('/cases')}
                        className="rounded-md border border-border bg-secondary px-4 py-2 text-sm hover:bg-secondary/80 transition-colors"
                      >
                        Back to cases
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {refreshErr && graph && (
                <div
                  role="alert"
                  data-testid="case-graph-refresh-error"
                  className="absolute top-3 left-1/2 -translate-x-1/2 z-30 max-w-xl flex items-center gap-3 text-xs bg-card border border-amber-500/50 rounded-md px-3 py-2 shadow-overlay"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500" />
                  <span className="text-foreground">Could not refresh the case graph: {refreshErr} Showing the last loaded graph.</span>
                  <button
                    type="button"
                    data-testid="case-graph-refresh-retry"
                    onClick={() => setGraphNonce((n) => n + 1)}
                    className="shrink-0 rounded border border-border bg-secondary px-2 py-1 font-medium hover:bg-secondary/80"
                  >
                    Retry
                  </button>
                </div>
              )}
              {graph && !err && (
                <FusionCanvas
                  key={`${id}:${graphLevel}:${graphVersion}:${withDemo}:${showActions}:${elements.length === 0 ? 'empty' : 'graph'}`}
                  elements={elements}
                  caseId={id}
                  graphLevel={graphLevel}
                  onExpandCluster={expandCluster}
                  revealKeys={pendingConsolFocus ?? undefined}
                  relatedCases={caseLinks}
                  remoteContext={graph.spine && !graph.spine.complete
                    ? { count: graph.spine.context_count, onLoad: () => { setContextRequested(true); setFullRequested(true); } }
                    : undefined}
                  showAllContext={contextRequested}
                />
              )}
              {showLegend && <FusionGraphLegend elements={elements} />}
              {emptyReal && !withDemo && tab !== 'import' && tab !== 'files' && (
                <div className="absolute inset-0 grid place-items-center bg-canvas/90 p-6">
                  <div className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg" data-testid="case-canvas-empty">
                    <p className="text-sm font-medium">No graph for this case yet</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Add your own evidence on <span className="font-medium text-foreground">Build</span>
                      {' '}(phone, bank statement, subpoena, Instagram handle, case notes) or import a
                      bank statement here. Demo surfaces stay off unless you check Add demo surfaces.
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => goMain('build')}
                        data-tour="case-canvas-empty-build"
                        className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-brand-gold-hover transition-colors"
                      >
                        Add evidence on Build
                      </button>
                      <button
                        type="button"
                        onClick={() => { setTab('import'); }}
                        className="rounded-md border border-border bg-secondary px-4 py-2 text-sm hover:bg-secondary/80 transition-colors"
                      >
                        Bank import
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {withDemo && (
                <div className="absolute top-3 right-3 z-20 text-[10px] font-medium text-amber-900 dark:text-amber-100 bg-amber-500/10 border border-amber-500/40 rounded-md px-2.5 py-1">
                  Synthetic demo surfaces overlaid (illustrative)
                </div>
              )}
              {dragOver && (
                <div className="absolute inset-3 z-30 rounded-lg border-2 border-dashed border-primary bg-primary/10 grid place-items-center pointer-events-none">
                  <div className="text-sm font-semibold text-primary bg-card border border-primary/40 rounded-md px-4 py-2 shadow-overlay">
                    Drop a bank statement to ingest → case file
                  </div>
                </div>
              )}
              <CaseReplay />
            </div>
          </FusionCanvasStage>
          <RunRail />
          <RadialMenu />
        </>
      )}
    </div>
  );
}
